import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldWarning } from "@phosphor-icons/react";
import { F, Stat, StaleResultPlaceholder } from "./primitives";

/* v2.87 · Arc-flash card — extracted from Calculators.jsx during the
   components/calculators/ refactor. Behaviour unchanged from v2.86. */
export function ArcFlashCard({ defaults = {}, compact = false }) {
  const [mode, setMode] = useState("simple");
  const [form, setForm] = useState({
    bolted_fault_ka: 25,
    clearing_time_s: 0.1,
    voltage_kv: 0.48,
    working_distance_mm: 455,
    gap_mm: 32,
    electrode_config: "VCB",
    enclosure_height_mm: 508,
    enclosure_width_mm: 508,
    enclosure_depth_mm: 508,
    grounded: true,
    ...defaults,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [hasComputed, setHasComputed] = useState(false);

  // v2.84 · Cross-tool bridge — one-click import of Bus bar geometry.
  const [busBarHint, setBusBarHint] = useState(null);
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem("eengdes:last_bus_bar_calc");
      if (raw) setBusBarHint(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  const importBusBar = () => {
    if (!busBarHint) return;
    const enclosureMap = {
      open_air: { electrode: "VOA", gap: 32 },
      ventilated_switchgear: { electrode: "VCB", gap: 32 },
      enclosed_switchgear: { electrode: "VCB", gap: 32 },
      ip54_enclosure: { electrode: "VCBB", gap: 25 },
      ip65_enclosure: { electrode: "VCBB", gap: 25 },
    };
    const m = enclosureMap[busBarHint.enclosure] || enclosureMap.enclosed_switchgear;
    const patch = { electrode_config: m.electrode, gap_mm: m.gap };
    if (busBarHint.isc_ka > 0) patch.bolted_fault_ka = busBarHint.isc_ka;
    if (busBarHint.sc_duration_s > 0) patch.clearing_time_s = busBarHint.sc_duration_s;
    setForm((prev) => ({ ...prev, ...patch }));
    setResult(null);
    toast.success(
      `Imported bus bar geometry · ${m.electrode} · gap ${m.gap} mm` +
      (busBarHint.isc_ka > 0 ? ` · Isc ${busBarHint.isc_ka} kA / ${busBarHint.sc_duration_s} s` : ""),
    );
  };

  const patchForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setResult(null);
  };
  const patchMode = (next) => {
    setMode(next);
    setResult(null);
  };

  const compute = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/calculators/arc-flash", { ...form, mode });
      setResult(data);
      setHasComputed(true);
      if (lastRun) {
        const parts = [];
        if (lastRun.inputs.bolted_fault_ka !== form.bolted_fault_ka) {
          parts.push(`kA ${lastRun.inputs.bolted_fault_ka} → ${form.bolted_fault_ka}`);
        }
        const iePrev = lastRun.result?.incident_energy_cal_cm2;
        const ieNew = data.incident_energy_cal_cm2;
        if (iePrev != null && ieNew != null
            && Math.abs(iePrev - ieNew) > 0.01) {
          parts.push(`IE ${iePrev.toFixed(2)} → ${ieNew.toFixed(2)} cal/cm²`);
        }
        if (lastRun.result?.ppe_category !== data.ppe_category) {
          parts.push(`PPE ${lastRun.result?.ppe_category ?? "—"} → ${data.ppe_category ?? "—"}`);
        }
        if (parts.length) toast.info(`Recomputed · ${parts.join(" · ")}`);
      }
      setLastRun({ inputs: { ...form }, result: data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-white/10 bg-[#12151C] p-6" data-testid="arcflash-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldWarning weight="duotone" size={18} className="text-[#FFCC00]" />
        <div className="label-tag">// Arc-flash · IEEE 1584-2018</div>
      </div>
      <h3 className="font-display text-2xl font-bold">Incident energy & PPE</h3>
      <p className="text-sm text-gray-400 mt-1">
        Computes incident energy (cal/cm²), arc-flash boundary, and NFPA 70E PPE category. Final labels must be reviewed by a licensed PE.
      </p>

      {busBarHint && (
        <div className="mt-4 border border-[#FFCC00]/40 bg-[#FFCC00]/[0.05] px-3 py-2 flex flex-wrap items-center gap-3"
          data-testid="af-busbar-import-banner">
          <div className="text-xs font-mono text-[#FFCC00] flex-1 min-w-0">
            Bus bar {busBarHint.recommended?.label} · {busBarHint.enclosure?.replace(/_/g, " ")}
            {busBarHint.isc_ka > 0 && <span> · Isc {busBarHint.isc_ka} kA / {busBarHint.sc_duration_s} s</span>}
          </div>
          <button onClick={importBusBar}
            className="border border-[#FFCC00]/60 text-[#FFCC00] px-3 py-1 text-xs font-mono hover:bg-[#FFCC00]/10"
            data-testid="af-busbar-import">
            Import bus bar geometry
          </button>
        </div>
      )}

      <div className="mt-4 inline-flex border border-white/10" role="tablist">
        {["simple", "full"].map((m) => (
          <button
            key={m}
            onClick={() => patchMode(m)}
            className={`px-4 py-2 text-sm font-mono ${mode === m ? "bg-[#00E5FF] text-black" : "text-gray-400 hover:text-white"}`}
            data-testid={`af-mode-${m}`}
          >
            {m.toUpperCase()}
          </button>
        ))}
      </div>

      <div className={`mt-5 grid ${compact ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3"} gap-3`}>
        <F label="Bolted fault kA">
          <input type="number" step="0.1" value={form.bolted_fault_ka} onChange={(e) => patchForm({ bolted_fault_ka: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="af-ibf" />
        </F>
        <F label="Clearing time s">
          <input type="number" step="0.01" value={form.clearing_time_s} onChange={(e) => patchForm({ clearing_time_s: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="af-time" />
        </F>
        <F label="Voltage kV">
          <input type="number" step="0.001" value={form.voltage_kv} onChange={(e) => patchForm({ voltage_kv: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="af-voltage" />
        </F>
        <F label="Working dist. mm">
          <input type="number" value={form.working_distance_mm} onChange={(e) => patchForm({ working_distance_mm: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="af-dist" />
        </F>
        {mode === "full" && (
          <>
            <F label="Gap mm">
              <input type="number" value={form.gap_mm} onChange={(e) => patchForm({ gap_mm: Number(e.target.value) })}
                className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                data-testid="af-gap" />
            </F>
            <F label="Electrode config">
              <select value={form.electrode_config} onChange={(e) => patchForm({ electrode_config: e.target.value })}
                className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                data-testid="af-elec">
                <option value="VCB">VCB — vertical in box</option>
                <option value="VCBB">VCBB — vertical, with barrier</option>
                <option value="HCB">HCB — horizontal in box</option>
                <option value="VOA">VOA — vertical open air</option>
                <option value="HOA">HOA — horizontal open air</option>
              </select>
            </F>
            <F label="Enclosure H × W × D mm">
              <div className="flex gap-1">
                <input type="number" value={form.enclosure_height_mm} onChange={(e) => patchForm({ enclosure_height_mm: Number(e.target.value) })}
                  className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none" />
                <input type="number" value={form.enclosure_width_mm} onChange={(e) => patchForm({ enclosure_width_mm: Number(e.target.value) })}
                  className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none" />
                <input type="number" value={form.enclosure_depth_mm} onChange={(e) => patchForm({ enclosure_depth_mm: Number(e.target.value) })}
                  className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none" />
              </div>
            </F>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={form.grounded} onChange={(e) => patchForm({ grounded: e.target.checked })}
                className="accent-[#00E5FF]"
                data-testid="af-grounded" />
              <span className="text-sm">Solidly grounded system</span>
            </label>
          </>
        )}
      </div>

      <button onClick={compute} disabled={busy} className="btn-primary mt-5" data-testid="af-compute">
        {busy ? "Computing…" : "Compute"}
      </button>

      {result && !result.error && (
        <div className="mt-6 border border-[#FFCC00]/40 bg-[#0A0C10] p-4 font-mono text-sm grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="af-result">
          <Stat label="Incident energy" value={`${result.incident_energy_cal_cm2} cal/cm²`} accent />
          <Stat label="Arc-flash boundary" value={`${result.arc_flash_boundary_mm} mm`} />
          {result.arcing_current_ka != null && (
            <Stat label="Arcing current" value={`${result.arcing_current_ka} kA`} />
          )}
          <div className="col-span-full p-3 border border-[#FFCC00]/60 bg-[#1A1500] text-[#FFCC00]" data-testid="af-ppe">
            <div className="label-tag !text-[#FFCC00]/80">// PPE</div>
            <div className="text-base font-display font-bold mt-1">{result.ppe_category}</div>
          </div>
          <div className="col-span-full text-gray-500 text-xs mt-2">
            <strong className="text-gray-300">Ref:</strong> {result.reference}
          </div>
        </div>
      )}
      {result?.error && (
        <div className="mt-6 p-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm">
          {result.error}
        </div>
      )}
      {!result && hasComputed && (
        <StaleResultPlaceholder tone="yellow" testid="af-result-stale" />
      )}
    </div>
  );
}
